import PropTypes from "prop-types";
import { Link, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import clsx from "clsx";
import { useState, useEffect, useRef } from "react";
import MenuCart from "./sub-components/MenuCart";
import { logout } from "../../store/slices/auth-slice";

const IconGroup = ({ iconWhiteClass }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { compareItems } = useSelector((state) => state.compare);
  const { wishlistItems } = useSelector((state) => state.wishlist);
  const { cartItems } = useSelector((state) => state.cart);
  const { products } = useSelector((state) => state.product);
  const { isAuthenticated, user } = useSelector((state) => state.auth);

  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isAccountOpen, setIsAccountOpen] = useState(false);
  const searchRef = useRef(null);
  const accountRef = useRef(null);

  useEffect(() => {
    if (!searchQuery || !products?.length) {
      setSuggestions([]);
      return;
    }
    const lower = searchQuery.toLowerCase();
    const matched = products
      .filter(
        (p) =>
          p.title?.toLowerCase().includes(lower) ||
          p.name?.toLowerCase().includes(lower) ||
          p.description?.toLowerCase().includes(lower)
      )
      .slice(0, 6);
    setSuggestions(matched);
  }, [searchQuery, products]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setSuggestions([]);
        setIsSearchOpen(false);
      }
      if (accountRef.current && !accountRef.current.contains(e.target)) {
        setIsAccountOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const trimmed = searchQuery.trim();
    if (trimmed.length > 0) {
      navigate(`/shop-grid-filter?search=${encodeURIComponent(trimmed)}`);
      setSuggestions([]);
      setIsSearchOpen(false);
    }
  };

  const handleSuggestionClick = (value) => {
    navigate(`/shop-grid-filter?search=${encodeURIComponent(value)}`);
    setSearchQuery("");
    setSuggestions([]);
    setIsSearchOpen(false);
  };

  const onLogout = () => {
    dispatch(logout());
    navigate("/login-register");
  };

  const triggerMobileMenu = () => {
    const offcanvasMobileMenu = document.querySelector("#offcanvas-mobile-menu");
    offcanvasMobileMenu.classList.add("active");
  };

  return (
    <div className={clsx("header-right-wrap", iconWhiteClass)}>
      <div className="same-style header-search d-none d-lg-block" ref={searchRef}>
        {isSearchOpen && (
          <div className="search-content active">
            <form onSubmit={handleSearchSubmit}>
              <input
                type="text"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
              />
              <button className="button-search" type="submit">
                <i className="pe-7s-search" />
              </button>
            </form>

            {suggestions.length > 0 && (
              <ul className="search-suggestions">
                {suggestions.map((s) => (
                  <li
                    key={s.id}
                    onClick={() => handleSuggestionClick(s.title || s.name)}
                  >
                    {s.title || s.name}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      <div
        className="same-style account-setting d-none d-lg-block"
        ref={accountRef}
      >
        <button
          className={clsx("account-setting-active", {
            active: isAccountOpen,
          })}
          onClick={() => setIsAccountOpen(!isAccountOpen)}
        >
          <i className="pe-7s-user-female" />
        </button>

        {isAccountOpen && (
          <div className="account-dropdown active">
            <ul>
              {isAuthenticated ? (
                <>
                  {user?.username && (
                    <li className="account-greeting">
                      Hi, <strong>{user.username}</strong>
                    </li>
                  )}
                  <li>
                    <Link to="/my-account">My account</Link>
                  </li>
                  <li>
                    <button onClick={onLogout}>Logout</button>
                  </li>
                </>
              ) : (
                <>
                  <li>
                    <Link to="/login-register">Login</Link>
                  </li>
                  <li>
                    <Link to="/login-register">Register</Link>
                  </li>
                </>
              )}
            </ul>
          </div>
        )}
      </div>

      <div className="same-style header-compare">
        <Link to="/compare">
          <i className="pe-7s-shuffle" />
          <span className="count-style">{compareItems?.length || 0}</span>
        </Link>
      </div>

      <div className="same-style header-wishlist">
        <Link to="/wishlist">
          <i className="pe-7s-like" />
          <span className="count-style">{wishlistItems?.length || 0}</span>
        </Link>
      </div>

      <div className="same-style cart-wrap d-none d-lg-block">
        <button
          className="icon-cart"
          onClick={() =>
            document
              .querySelector(".shopping-cart-content")
              ?.classList.toggle("active")
          }
        >
          <i className="pe-7s-shopbag" />
          <span className="count-style">{cartItems?.length || 0}</span>
        </button>
        <MenuCart />
      </div>

      <div className="same-style cart-wrap d-block d-lg-none">
        <Link className="icon-cart" to="/cart">
          <i className="pe-7s-shopbag" />
          <span className="count-style">{cartItems?.length || 0}</span>
        </Link>
      </div>

      <div className="same-style mobile-off-canvas d-block d-lg-none">
        <button className="mobile-aside-button" onClick={triggerMobileMenu}>
          <i className="pe-7s-menu" />
        </button>
      </div>
    </div>
  );
};

IconGroup.propTypes = { iconWhiteClass: PropTypes.string };
export default IconGroup;
